import type { ApplogValue, ArrayElementType, EntityID, Thread, ThreadOnlyCurrentNoDeleted, ValueOrMatcher } from '@wovin/core'
import type { Accessor } from 'solid-js'
import type { TypeAttrOptions, TypeMapKeys } from '../data/VMs/utils-typemap'
import type { RelationModelDef } from './../data/Relations'
import type { QueryNode } from '@wovin/core/query'
import { lazyVal } from '@note3/utils'
import { useLocation, useParams } from '@solidjs/router'
import {
	assertRaw,
	createDebugName,
	isInitEvent,
	isoDateStrCompare,
	lastWriteWins,
	liveFilterAndMap,
	liveQuery,
	liveQueryEntity,
	liveQueryNot,
	query,
	queryAndMap,
	withoutDeleted,
} from '@wovin/core'
import { Logger } from 'besonders-logger'
import { createMemo, createSignal, getOwner, mapArray, onCleanup } from 'solid-js'
import {
	ThreadContext,
	useLiveFilterAndMap,
	useLiveQuery,
	useLiveQueryAndMap,
	useLiveQueryEntity,
	useRollingAcc,
	useSubscribable,
	useSubscribableValue,
	useThread,
	withThread,
} from '@wovin/hooks-solid'
import { getApplogDB, insertApplogs } from '../data/ApplogDB'
import { getRootProviders, getVirtualRootDiscoveryPatterns } from '../plugins/registry'
import { makeWovinApi } from '../plugins/seam/context'
import { getBlocksWithTags } from '../data/block-utils-nowin'
import { REL_DEF } from '../data/data-types'
import {
	RE_AT_TAG_ONLY,
	RE_AT_TAG_WITHCONTEXT,
	RE_HASH_TAG_ONLY,
	RE_HASH_TAG_WITHCONTEXT,
	RE_PLUS_TAG_ONLY,
	RE_PLUS_TAG_WITHCONTEXT,
} from '../data/note3-regex-constants'
import { contentVlToPlaintext, rawContentMatchTag } from '../data/note3-utils-nodeps'
import { orderBlockRelations } from '../data/relation-utils'
import { doesContentMatchSearch } from '../data/search'
import { getProviderIDs } from '../data/storage-hooks'
import { BlockVM, useBlk } from '../data/VMs/BlockVM'
import { RelationVM, useRel } from '../data/VMs/RelationVM'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO, { prefix: '[rx]' })

export { ThreadContext, useThread, withThread } from '@wovin/hooks-solid'

export function useFocus() {
	const params = useParams()
	return createMemo(() => params.blockID as EntityID | null)
}

export function useIsTimeline() {
	const location = useLocation()
	return createMemo(() => location.pathname === '/timeline')
}

export function useRawThread() {
	return assertRaw(useThread())
}
export function useThreadWithFilters(
	{ withHistory, withDeleted }: { withHistory?: boolean; withDeleted?: boolean }, // no default as useCurrentThread is meant for that purpose
	thread = useThread(),
) {
	if (!withHistory && !thread.filters.includes('lastWriteWins')) thread = lastWriteWins(thread) // HACK: proper divergence tracking should be done
	if (!withDeleted && !thread.filters.includes('withoutDeleted')) thread = withoutDeleted(thread)
	return thread
}
export function useCurrentThread(thread = useThread()) {
	return useThreadWithFilters({ withHistory: false, withDeleted: false }, thread) as ThreadOnlyCurrentNoDeleted
}
export const useReadOnlyState = () => createMemo(() => useThread().readOnly)

/////////////////
// NEW QUERIES //
/////////////////

/** @DEPRECATED -> useBlk */
export function useBlockVM(blockID: EntityID) {
	const ds = useRawThread()
	DEBUG(`[useBlockVM]`, blockID, ds)
	return BlockVM.get(blockID, ds)
}

export function useRelationVM(relationID: EntityID) {
	const ds = useRawThread()
	DEBUG(`[useRelationVM]`, relationID, ds)
	return RelationVM.get(relationID, ds)
}
export function useRelation(relationID: EntityID) {
	const ds = useCurrentThread()
	DEBUG(`[useRelation]`, relationID, ds)
	const entity = useLiveQueryEntity('relation', relationID, REL_DEF._attrs, { thread: ds })
	return createMemo(() => {
		const relation = entity()
		if (!relation) return null
		return ({
			en: relationID,
			...relation,
		} as unknown as RelationModelDef)
	})
}

export function useBlockAt<wType extends TypeMapKeys = 'Block'>(blockID: EntityID, at: TypeAttrOptions<wType>) {
	/* TODO: BLOCK._attrs */
	const ds = useCurrentThread()
	const atStr = at.toString()
	DEBUG(`[useBlockAt]`, ds.name, { ds, blockID, at })
	const entity = useLiveQueryEntity('block', blockID, [atStr], { thread: ds })
	return createMemo(() => {
		const value = entity()
		VERBOSE(`[useBlockAt] query result`, value)
		return value?.[atStr]
	})
}

export function useEntityAt<T extends ApplogValue>(id: EntityID, at: string, defaultVal: T = null) {
	const thread = useCurrentThread()
	DEBUG(`[useEntityAt]`, thread.nameAndSizeUntracked, { ds: thread, id, at })

	const results = useLiveFilterAndMap({ en: id, at }, 'vl', { thread })
	return [
		() => {
			const resultArr = results()
			return resultArr.length ? resultArr[0] as T : defaultVal
		},
		(newVal: T) => insertApplogs(thread, { en: id, at, vl: newVal }),
	] as const
}
export function useEntityAttrs<ATTRS extends readonly string[], VALUES extends Record<ArrayElementType<ATTRS>, ApplogValue>>(
	en: EntityID,
	attrs: ATTRS,
	defaultVals: Partial<VALUES> = {},
) {
	type ATTR = ArrayElementType<ATTRS>
	const thread = useCurrentThread() // HACK based on last write wins
	DEBUG(`[useEntityAttrs]`, thread.nameAndSizeUntracked, { ds: thread, id: en, at: attrs })

	const mapped = useRollingAcc<Map<ATTR, ApplogValue>>(new Map<ATTR, ApplogValue>(), (event, map) => {
		if (!isInitEvent(event)) {
			for (const removed of event.removed ?? []) {
				const deleted = map.delete(removed.at as ATTR)
				if (!deleted) WARN(`[useEntityAttrs] event.removed did not exist in our map`, { removed, event, map: map.entries() })
			}
		}
		for (const added of (isInitEvent(event) ? event.init : event.added)) {
			map.set(added.en as ATTR, added.ts)
		}
	}, { thread, name: createDebugName({ thread, args: { id: en, at: attrs } }) })

	return [
		createMemo(() => Object.fromEntries(mapped().entries())) as unknown as Record<ArrayElementType<ATTRS>, ApplogValue>,
		(at: ATTR, newVal: ApplogValue) => {
			return insertApplogs(thread, { en, at, vl: newVal })
		},
	] as const
}

export function useKidRelationIDs(blockID: EntityID) {
	if (!blockID) throw ERROR(`[useKidRelations] Invalid blockID:`, blockID) // TS doesn't save us in all cases
	const thread = useCurrentThread()
	DEBUG(`[useKidRelationIDs#${blockID}]`, blockID, thread)
	return useLiveFilterAndMap({ at: REL_DEF.childOf, vl: blockID }, 'en', { thread })
}

export function useKidRelations(blockID: EntityID) {
	DEBUG(`[useKidRelations#${blockID}]`, blockID)
	const rawThread = useRawThread()
	const relationsQuery = useKidRelationIDs(blockID)
	VERBOSE(`[useKidRelations#${blockID}] relationsQuery:`, relationsQuery)
	const relations = mapArray(relationsQuery, relID => useRel(relID, rawThread))
	return createMemo(() => {
		VERBOSE(`[useKidRelations#${blockID}] relations:`, relations())
		return orderBlockRelations(relations())
	})
}

export function useKidVMs(blockID: EntityID) {
	DEBUG(`[useKidVMs#${blockID}]`, blockID)
	const rawThread = useRawThread()
	const relationDefs = useKidRelations(blockID)
	VERBOSE(`[useKidVMs#${blockID}] relationsQuery:`, relationDefs)
	return mapArray(relationDefs, ({ block }) => useBlk(block as string, rawThread))
}

export function usePlacementRelationIDs(blockID: EntityID) {
	DEBUG(`[useParentRelationIDs]`, blockID, REL_DEF.block)
	const ds = useCurrentThread()
	return useLiveFilterAndMap({ vl: blockID, at: REL_DEF.block }, 'en', { thread: ds })
}

// returns RelationDefs for each Placement of blockID
export function usePlacementRelations(blockID: EntityID) {
	DEBUG(`[usePlacementRelations]`, blockID)
	const ds = useCurrentThread()
	const relationIDs = usePlacementRelationIDs(blockID)
	return mapArray(relationIDs, (relID) => {
		const rel = useRel(relID, ds)
		VERBOSE('usePlacementRelations', { relID, rel })
		return rel
	})
}

export function useRelAt(relID: EntityID, at: string /* TODO: BLOCK._attrs */) {
	DEBUG(`[useRelAt]`, relID)
	const db = useCurrentThread()
	const entity = useLiveQueryEntity('relation', relID, [at], { thread: db })
	return createMemo(() => entity()?.[at])
}
export function useParents(blockID: EntityID, thread?: Thread) {
	DEBUG(`[useParent]`, blockID)
	if (!blockID) throw ERROR(`[useParent] empty argument:`, blockID)
	const db = thread ? useCurrentThread(thread) : useCurrentThread()
	const parents = useLiveQueryAndMap(
		[
			{ en: '?relID', at: REL_DEF.block, vl: blockID },
			{ en: '?relID', at: REL_DEF.childOf, vl: '?parentID' },
		],
		'parentID',
		{ thread: db },
	)
	VERBOSE.isEnabled && VERBOSE(`[useParent] parents:`, parents)
	return parents as Accessor<readonly string[]>
}

// TODO: computedFnDeepCompare?
export function useRoots() {
	const thread = useCurrentThread()
	DEBUG(`[useRoots] on`, thread.nameAndSizeUntracked)

	// Create a live query for all blocks OUTSIDE the memo
	// This persists across memo re-runs and feeds into liveQueryNot
	const blocksLiveQuery = liveQuery(thread, [
		{ en: '?blockID', at: 'block/content' }, // This is (sort of) the core of makes an entity a block
	])

	// Use liveQueryNot to filter out blocks that have parents
	// This returns a LiveQueryResult which wraps a live SubscribableArray
	const rootsLiveQuery = liveQueryNot(thread, blocksLiveQuery, {
		en: '?relID',
		at: 'relation/block',
		vl: '?blockID',
	})

	// Bridge the liveQueryNot result to a Solid accessor by subscribing to the LiveQueryResult
	const [rootNodes, setRootNodes] = createSignal<readonly QueryNode[]>(rootsLiveQuery.nodes, { equals: false })
	const unsub = rootsLiveQuery.subscribe(() => {
		setRootNodes(() => rootsLiveQuery.nodes)
	})

	// ── Virtual root discovery: plugin patterns + RootProviders (seam v1.1) ──
	// (was: hardcoded media/type — now consumes what plugins declare)
	const discoveryPatterns = [
		...getVirtualRootDiscoveryPatterns(),
		...getRootProviders().flatMap(p => p.patterns ?? []),
	].filter(p => typeof p.at === 'string') // v1: literal-attr patterns only (indexable)
	const virtualIdAccessors = discoveryPatterns.map((pattern) => {
		const vlIsVariable = typeof pattern.vl === 'string' && pattern.vl.startsWith('?')
		const filterPattern = pattern.vl !== undefined && !vlIsVariable
			? { at: pattern.at as string, vl: pattern.vl }
			: { at: pattern.at as string }
		return useLiveFilterAndMap(
			filterPattern,
			(log: { en: EntityID | undefined; at?: string; vl?: ApplogValue }) => log.en ?? '',
			{ thread },
		)
	})
	const providedRootAccessors = getRootProviders()
		.filter(p => p.provide)
		.map(p => p.provide!(makeWovinApi(() => thread)))

	if (getOwner()) {
		onCleanup(() => {
			unsub()
			rootsLiveQuery.dispose()
			blocksLiveQuery.dispose()
		})
	}

	const firstLogsThread = lastWriteWins(useThreadWithFilters({ withHistory: true }), {
		inverseToOnlyReturnFirstLogs: true,
		tolerateAlreadyFiltered: true,
	})

	const blockCreationMap = useRollingAcc<Map<EntityID, string>>(
		new Map<EntityID, string>(),
		function blockCreationMapRollingAcc(event, map) {
			if (!isInitEvent(event)) {
				for (const removed of event.removed ?? []) {
					if (removed.at !== 'block/content') continue
					map.delete(removed.en)
				}
			}
			for (const added of (isInitEvent(event) ? event.init : event.added)) {
				if (added.at !== 'block/content') continue
				map.set(added.en, added.ts)
			}
		},
		{ thread: firstLogsThread },
	)

	// Sort the roots by creation time
	return createMemo(() => {
		const currentRootNodes = rootNodes()
		const creationMap = blockCreationMap()
		DEBUG(`[useRoots] roots:`, currentRootNodes, thread)

		const sortedRecords = currentRootNodes.slice().sort((nodeA, nodeB) => {
			const blockA = nodeA.variables.blockID as EntityID
			const blockB = nodeB.variables.blockID as EntityID
			const firstLogA = creationMap.get(blockA)
			const firstLogB = creationMap.get(blockB)
			return isoDateStrCompare(firstLogA, firstLogB, 'desc') // sort by first log time - i.e. creation
		})
		const result = sortedRecords.map(({ variables }) => variables.blockID as string)

		// Append virtual roots from plugin discovery patterns + RootProviders
		const virtualIds = virtualIdAccessors.flatMap(acc => [...acc()])
		for (const id of virtualIds) {
			if (id && !result.includes(id)) result.push(id)
		}
		for (const providedAcc of providedRootAccessors) {
			for (const descriptor of providedAcc()) {
				if (descriptor?.id && !result.includes(descriptor.id)) result.push(descriptor.id)
			}
		}

		DEBUG(`[useRoots] result:`, thread.nameAndSizeUntracked, { sortedRecords, virtualIds: [...virtualIds] })
		return result
	})
}
export function useBlocksMatchingVl(vlMatch: any) {
	const thread = useCurrentThread()
	const blocks = useLiveQuery([
		{ en: '?blockID', at: 'block/content', vl: vlMatch },
	], { thread })
	return createMemo(() => {
		return blocks().map(node => node.variables.blockID as EntityID).reverse() // HACK to get newest first //TODO: actually sort
	})
}
export function useBlocksMatchingSearch(search: string, thread?: Thread) {
	thread ??= useCurrentThread()
	const blocks = useLiveQuery([
		{ en: '?blockID', at: 'block/content', vl: vl => doesContentMatchSearch(contentVlToPlaintext(vl as string), search) },
	], { thread })
	return createMemo(() => {
		return blocks().map(node => node.variables.blockID as EntityID).reverse() // HACK to get newest first //TODO: actually sort
	})
}

/** Snapshot (non-reactive) variant of useBlocksMatchingSearch. Use in imperative/async contexts. */
export function getBlocksMatchingSearch(search: string, thread: Thread): EntityID[] {
	const result = query(thread, [
		{ en: '?blockID', at: 'block/content', vl: vl => doesContentMatchSearch(contentVlToPlaintext(vl as string), search) },
	])
	return result.records.map(r => r.blockID as EntityID).reverse() // HACK to get newest first
}
export function useBlocksWithTags(tags: readonly string[]) {
	const thread = useCurrentThread()
	const blocks = getBlocksWithTags(thread, tags)
	return blocks
}

export function useRootsOfMaybeNested(blocks: readonly EntityID[], thread?: Thread) {
	if (!blocks) return null
	const db = thread ? useCurrentThread(thread) : useCurrentThread()
	const roots = new Set<EntityID>()
	const checkBlockParent = (block: EntityID, currentParent?: EntityID) => {
		const parents = queryAndMap(db, [
			{ en: '?relID', at: REL_DEF.block, vl: currentParent ?? block },
			{ en: '?relID', at: REL_DEF.childOf, vl: '?parentID' },
		], 'parentID') as string[]
		if (!parents.length) roots.add(block)
		for (const parent of parents) {
			if (roots.has(parent)) return
			checkBlockParent(block, parent)
		}
	}
	for (const block of blocks) {
		checkBlockParent(block)
	}
	return [...roots]
}

export function useHashTags(thread?: Thread) {
	return useTagsGeneric(RE_HASH_TAG_ONLY, RE_HASH_TAG_WITHCONTEXT, thread)
}
export function usePlusTags(thread?: Thread) {
	return useTagsGeneric(RE_PLUS_TAG_ONLY, RE_PLUS_TAG_WITHCONTEXT, thread)
}
export function useAtTags(thread?: Thread) {
	return useTagsGeneric(RE_AT_TAG_ONLY, RE_AT_TAG_WITHCONTEXT, thread)
}
export function useAllTags(thread?: Thread) {
	const hashTags = useHashTags(thread)
	const plusTags = usePlusTags(thread)
	const atTags = useAtTags(thread)
	return createMemo(() => {
		const entries = [
			...[...hashTags().entries()].map(([k, v]) => [`#${k}`, v] as const),
			...[...plusTags().entries()].map(([k, v]) => [`+${k}`, v] as const),
			...[...atTags().entries()].map(([k, v]) => [`@${k}`, v] as const),
		]
		VERBOSE(`[useAllTags]`, entries)
		return new Map(entries)
	})
}

export function useTagsGeneric(regexCheap: RegExp, regex: RegExp, thread?: Thread): Accessor<Map<string, number>> {
	thread ??= useCurrentThread()
	DEBUG(`[useTagsGeneric] searching`, thread.nameAndSizeUntracked, { regex, regexCheap })
	const blocks = liveQuery(thread, [
		{ en: '?blockID', at: 'block/content' },
	])

	const tagsMap = useRollingAcc<Map<string, number>>(
		new Map<string, number>(),
		(event, map) => {
			DEBUG(`[useTagsGeneric] parent thread update:`, { event, map })
			if (!isInitEvent(event)) {
				for (const removed of event.removed ?? []) {
					const tag = rawContentMatchTag(regexCheap, regex, removed.vl)
					if (tag) {
						const count = map.get(tag)
						if (!count) throw ERROR(`count`, count, { map, event })
						VERBOSE(`[useTagsGeneric] decreasing:`, blocks)
						if (count > 1) {
							map.set(tag, count - 1)
						} else {
							map.delete(tag)
						}
					}
				}
			}
			for (const added of (isInitEvent(event) ? event.init : event.added)) {
				const tag = rawContentMatchTag(regexCheap, regex, added.vl)
				if (tag) {
					const count = map.get(tag) ?? 0
					map.set(tag, count + 1)
				}
			}
		},
		{ thread: blocks.leafNodeThread, name: `useTags{${regexCheap.source}}` },
	)

	return tagsMap
}

// ============================================================================
// NON-REACTIVE TAG QUERIES (Performance workaround)
// ============================================================================
// These query tags once and cache forever. No reactivity = no performance hit.
// Trade-off: New tags won't appear until page refresh.

let cachedHashTags: Map<string, number> | null = null
let cachedPlusTags: Map<string, number> | null = null
let cachedAtTags: Map<string, number> | null = null

function getTagsOnce(thread: Thread, regexCheap: RegExp, regex: RegExp): Map<string, number> {
	DEBUG(`[getTagsOnce] querying once`, thread.nameAndSizeUntracked, { regex })

	// Query all blocks with content — one-off, no reactivity
	const blocks = query(thread, [
		{ en: '?blockID', at: 'block/content', vl: '?content' },
	])
	const records = blocks.records.slice()
	DEBUG(`[getTagsOnce] query result`, { sample: records.slice(0, 3) })

	const tagsMap = new Map<string, number>()

	for (const record of records) {
		const content = (record?.content ?? record?.vl) as string
		if (!content) {
			continue
		}
		const tag = rawContentMatchTag(regexCheap, regex, content)
		if (tag) {
			const count = tagsMap.get(tag) ?? 0
			tagsMap.set(tag, count + 1)
		}
	}

	DEBUG(`[getTagsOnce] found ${tagsMap.size} unique tags`, Array.from(tagsMap.keys()).slice(0, 20))
	return tagsMap
}

export function getLazyHashTags(thread: Thread): Map<string, number> {
	if (!cachedHashTags) {
		cachedHashTags = getTagsOnce(thread, RE_HASH_TAG_ONLY, RE_HASH_TAG_WITHCONTEXT)
	} else {
		DEBUG(`[getLazyHashTags] reused cache`, cachedHashTags.size)
	}
	return cachedHashTags
}

export function getLazyPlusTags(thread: Thread): Map<string, number> {
	if (!cachedPlusTags) {
		cachedPlusTags = getTagsOnce(thread, RE_PLUS_TAG_ONLY, RE_PLUS_TAG_WITHCONTEXT)
	} else {
		DEBUG(`[getLazyPlusTags] reused cache`, cachedPlusTags.size)
	}
	return cachedPlusTags
}

export function getLazyAtTags(thread: Thread): Map<string, number> {
	if (!cachedAtTags) {
		cachedAtTags = getTagsOnce(thread, RE_AT_TAG_ONLY, RE_AT_TAG_WITHCONTEXT)
	} else {
		DEBUG(`[getLazyAtTags] reused cache`, cachedAtTags.size)
	}
	return cachedAtTags
}
